import { Button, Card, CardBody, CardHeader, type ColumnDef, DataTable, EmptyState, InlineMessage, Input, Spinner, Stack, Text } from '@olytic/foundation';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { formatMoney, formatNumber } from '../objects/format';
import { addQuantity, type DraftCell, isDirty, type PriceGridPayload, problems, setCell, toDraft, toWire } from './priceGridEditor';

async function send(url: string, init?: RequestInit): Promise<PriceGridPayload> {
  const res = await authenticatedFetch(url, init);
  const data = (await res.json().catch(() => null)) as (PriceGridPayload & { error?: string }) | null;
  if (!res.ok) throw new Error(data?.error ?? `Could not load the cells (${res.status}).`);
  return data as PriceGridPayload;
}

/**
 * A Price Grid's cells as one editable grid, under its details: a row for
 * every Company Size (and quantity, on a quantity grid). Amount, hours and
 * label edit in place across as many rows as needed, and Save sends the
 * whole set; a row left without an amount is an option not offered.
 */
export function PriceGridCellsEditor({ gridId, onSaved }: { gridId: string; onSaved?: () => void }): JSX.Element {
  const url = `/api/price-grids/${encodeURIComponent(gridId)}/cells`;
  const [data, setData] = useState<PriceGridPayload | null>(null);
  const [draft, setDraft] = useState<DraftCell[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [quantity, setQuantity] = useState('');

  const accept = useCallback((payload: PriceGridPayload) => {
    setData(payload);
    setDraft(toDraft(payload));
  }, []);
  const reload = useCallback(() => {
    setError(null);
    send(url).then(accept).catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load the cells.'));
  }, [url, accept]);
  useEffect(reload, [reload]);

  const dirty = data !== null && isDirty(draft, data);
  const issues = problems(draft);
  const byQuantity = data?.grid.prices_by === 'company_size_and_quantity';
  const quantityLabel = data?.grid.quantity_label ?? 'Quantity';

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      accept(await send(url, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ cells: toWire(draft) }) }));
      onSaved?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the cells.');
    } finally {
      setSaving(false);
    }
  }

  function add(): void {
    const n = Number(quantity);
    if (!data || !(n > 0)) return;
    setDraft((rows) => addQuantity(rows, data, n));
    setQuantity('');
  }

  const columns = useMemo<ColumnDef<DraftCell>[]>(
    () => [
      ...(byQuantity ? [{ key: 'second', label: quantityLabel, nowrap: true, get: (r: DraftCell) => r.second ?? '' }] : []),
      { key: 'first', label: data?.grid.prices_by ? 'Company Size' : 'Option', nowrap: true, get: (r) => (data?.grid.prices_by ? r.first : [r.first, r.second].filter(Boolean).join(' · ')) },
      {
        key: 'amount', label: 'Amount', align: 'right', nowrap: true,
        get: (r) => (r.amount === null ? 'Not offered' : formatMoney(r.amount)),
        editor: { kind: 'number', min: 0, step: 1, nullable: true, getValue: (r) => (r.amount === null ? '' : String(r.amount)) },
      },
      {
        key: 'hours', label: 'Hours', align: 'right', nowrap: true,
        get: (r) => (r.hours === null ? '—' : formatNumber(r.hours)),
        editor: { kind: 'number', min: 0, step: 0.5, nullable: true, getValue: (r) => (r.hours === null ? '' : String(r.hours)) },
      },
      {
        key: 'label', label: 'Label', get: (r) => r.label || '—',
        editor: { kind: 'text', nullable: true, getValue: (r) => r.label },
      },
    ],
    [byQuantity, quantityLabel, data],
  );

  return (
    <Card>
      <CardHeader
        title="Cells"
        actions={
          dirty ? (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => data && setDraft(toDraft(data))} disabled={saving}>Discard</Button>
              <Button size="sm" variant="brand" onClick={() => void save()} loading={saving} disabled={issues.length > 0}>Save</Button>
            </div>
          ) : undefined
        }
      />
      <CardBody>
        {!data && !error ? <Spinner label="Loading the cells…" /> : null}
        {data ? (
          <Stack>
            <Text tone="muted">
              Each row is one option on the menu: its fixed amount and the hours it takes. Edit as many as you like, then save them
              together. A row with no amount is not offered.
            </Text>
            {issues.length ? (
              <InlineMessage tone="warning" role="status">
                {issues.slice(0, 3).join(' ')}
                {issues.length > 3 ? ` And ${issues.length - 3} more.` : ''}
              </InlineMessage>
            ) : null}
            <DataTable<DraftCell>
              columns={columns}
              rows={draft}
              getRowId={(r) => r.key}
              density="compact"
              responsive={false}
              onCellSave={(r, key, value) => setDraft((rows) => setCell(rows, r.key, key as 'amount' | 'hours' | 'label', value))}
              empty={
                <EmptyState
                  title={byQuantity ? 'No quantities yet' : 'No cells'}
                  description={byQuantity ? `Add a quantity, and fill in its amount and hours for each Company Size.` : 'This grid has no cells.'}
                />
              }
              footerRow={
                byQuantity ? (
                  <div className="flex items-center gap-2">
                    <div className="w-32">
                      <Input
                        aria-label={`Add ${quantityLabel}`}
                        type="number"
                        min={1}
                        step={1}
                        value={quantity}
                        placeholder={quantityLabel}
                        onChange={(e) => setQuantity(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') add();
                        }}
                      />
                    </div>
                    <Button variant="subtle" size="sm" onClick={add} disabled={!(Number(quantity) > 0)}>
                      Add a Quantity
                    </Button>
                  </div>
                ) : undefined
              }
            />
          </Stack>
        ) : null}
        {error ? (
          <InlineMessage tone="error" role="alert" className="mt-3">
            {error}{' '}
            <Button variant="subtle" size="sm" onClick={reload}>Reload</Button>
          </InlineMessage>
        ) : null}
      </CardBody>
    </Card>
  );
}
